import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Table from '@risksmart-app/components/src/table';
import { type FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import ExportButton from 'src/components/export-button';
import { PageLayout } from 'src/layouts';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useDeleteCustomDatasources } from '@/hooks/mutations/custom-datasource/useDeleteCustomDatasources';
import { useGetCustomDatasources } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import { getCounter } from '@/utils/collectionUtils';
import { handleError } from '@/utils/errorUtils';
import { addCustomDatasourceUrl } from '@/utils/urls';

import { useGetCollectionTableProps } from './config';
import { CustomDatasourceRegisterActions } from './CustomDatasourceRegisterActions';
import { CustomDatasourceRegisterToolbar } from './CustomDatasourceRegisterToolbar';
import { CUSTOM_DATASOURCE_SMART_COPY } from './customDatasourceSmartCopy';
import type { CustomDatasourceTableFields } from './types';

const Page: FC = () => {
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'customDatasources',
  });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const navigate = useNavigate();

  // `refetch` is what refreshes the table after a delete. The register read is
  // `no-cache` (the `createQueryHook` default), so the delete hook's Apollo
  // `evictFields` has nothing to evict and cannot trigger a re-read — without
  // this the deleted rows sit there until the page remounts.
  const { data, loading, refetch } = useGetCustomDatasources({ queryArgs: {} });

  const tableProps = useGetCollectionTableProps(data?.custom_datasource);

  const title = st('register_title');
  const counter = getCounter(tableProps.totalItemsCount, loading);
  const openCreate = useCallback(() => {
    navigate(addCustomDatasourceUrl());
  }, [navigate]);

  // Reads OpenFeature (flagd locally/eph, Datadog on dev-cloud/prod), or the
  // seeded Meta.features override for e2e test orgs.
  const isSmartCopyEnabled = useIsFeatureFlagEnabled(
    CUSTOM_DATASOURCE_SMART_COPY.flagKey
  );

  const { hasPermission: canInsert, loading: canInsertLoading } =
    useHasPermissionQuery('insert:custom_datasource');
  const { hasPermission: canDelete, loading: canDeleteLoading } =
    useHasPermissionQuery('delete:custom_datasource');

  const [selected, setSelected] = useState<CustomDatasourceTableFields[]>([]);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  // Delete needs selection whether or not Smart Copy is on — Copy is not its only
  // consumer any more. Mirrors the Indicator register's gate.
  const showSelection =
    (canDelete && !canDeleteLoading) ||
    (isSmartCopyEnabled && canInsert && !canInsertLoading);

  const { deleteCustomDatasources, loading: deleteLoading } =
    useDeleteCustomDatasources();

  // The rows are already deleted by the time this runs, so a failed refresh is a
  // stale table — not a failed delete. Reporting it through the caller would
  // reject `asyncAction`, which shows "Failed to save changes" for a delete that
  // succeeded and leaves the modal open on rows that no longer exist.
  const refreshRegister = async () => {
    try {
      await refetch();
    } catch (error) {
      handleError(error);
    }
  };

  const onDelete = useDeleteResultNotification({
    entityName: st('entity_name'),
    asyncAction: async () => {
      try {
        await deleteCustomDatasources({ Ids: selected.map((s) => s.Id) });
      } catch (error) {
        // The table may now be lying — a concurrent delete 404s, a timeout
        // leaves it unknown. Refresh here rather than in `failureAction`, which
        // only runs when the action resolves falsy; rethrow keeps the toast on
        // the server's message.
        void refreshRegister();

        throw error;
      }

      setSelected([]);
      setIsDeleteModalVisible(false);
      await refreshRegister();

      return true;
    },
  });

  const openDeleteModal = useCallback(() => {
    setIsDeleteModalVisible(true);
  }, []);

  // Derived once: the button surface wants "enabled", the toolbar action wants
  // "disabled", and inverting in two places invites the two drifting apart.
  const hasSelection = selected.length > 0;

  const registerActions = (
    <CustomDatasourceRegisterActions
      isSmartCopyEnabled={isSmartCopyEnabled}
      selectedCount={selected.length}
      // Only ever the sole selection — never the first of many.
      selectedId={selected.length === 1 ? selected[0].Id : undefined}
      canInsert={canInsert}
      canDelete={canDelete}
      isDeleteEnabled={hasSelection}
      onDelete={openDeleteModal}
    />
  );

  return (
    <PageLayout
      helpTranslationKey={'customDatasources.registerHelp'}
      title={title}
      counter={counter}
      // Object-level toolbar (CUS-172) — flag-gated. Mounted only when the flag
      // is on, so `useExportOptions` runs on exactly one path (toolbar vs the
      // legacy ExportButton) rather than both.
      headerOverride={
        objectToolbarEnabled ? (
          <CustomDatasourceRegisterToolbar
            title={title}
            counter={counter}
            tableProps={tableProps}
            onCreate={openCreate}
            hasDeletePermission={canDelete}
            isDeleteDisabled={!hasSelection}
            onDelete={openDeleteModal}
          />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <ExportButton tableProps={tableProps} entityLabel={title} />
          {/* Smart Copy off → the action controls stay up here. On → they move
              onto the table tools row below, beside the filter. */}
          {!isSmartCopyEnabled && registerActions}
        </SpaceBetween>
      }
      meta={{
        title: st('register_title'),
      }}
    >
      <Table
        {...tableProps}
        loading={loading}
        filter={
          isSmartCopyEnabled ? (
            <div className={'flex flex-1 items-start gap-3'}>
              <div className={'flex-1'}>{tableProps.filter}</div>
              {registerActions}
            </div>
          ) : (
            tableProps.filter
          )
        }
        selectionType={showSelection ? 'multi' : undefined}
        selectedItems={selected}
        trackBy={'Id'}
        onSelectionChange={({ detail }) => setSelected(detail.selectedItems)}
      />
      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={st('confirm_delete_bulk_title')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {st('confirm_delete_bulk_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
